import React, { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Loader2, LifeBuoy } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import api from "../lib/api";
import { useSettings } from "../contexts/SettingsContext";

const SESSION_KEY = "hp_support_session";
const MSGS_KEY = "hp_support_msgs";

const newSessionId = () =>
    `sup_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const QUICK_PROMPTS = [
    "Where's my order?",
    "How do I become a distributor?",
    "Shipping & charges?",
    "How to consult a doctor?",
];

export default function SupportChatBot() {
    const { settings } = useSettings();
    const { pathname } = useLocation();
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [draft, setDraft] = useState("");
    const [escalated, setEscalated] = useState(false);
    const [sessionId] = useState(() => {
        let s = localStorage.getItem(SESSION_KEY);
        if (!s) {
            s = newSessionId();
            localStorage.setItem(SESSION_KEY, s);
        }
        return s;
    });
    const [messages, setMessages] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem(MSGS_KEY) || "[]");
        } catch {
            return [];
        }
    });

    const scrollRef = useRef(null);

    useEffect(() => {
        localStorage.setItem(MSGS_KEY, JSON.stringify(messages));
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages, open]);

    // Hide on admin / employee back-office pages — keep on public + customer pages.
    if (
        pathname.startsWith("/admin") ||
        pathname.startsWith("/employee") ||
        pathname.startsWith("/distributor/admin") ||
        pathname.startsWith("/invoice/")
    ) {
        return null;
    }

    const send = async (text) => {
        const message = (text ?? draft).trim();
        if (!message || busy) return;
        setDraft("");
        setMessages((m) => [...m, { role: "user", content: message }]);
        setBusy(true);
        try {
            const { data } = await api.post("/ai/support-chat", {
                session_id: sessionId,
                message,
            });
            setMessages((m) => [
                ...m,
                {
                    role: "assistant",
                    content: data.reply,
                    escalate: data.escalate,
                    topic: data.topic,
                },
            ]);
            if (data.escalate) setEscalated(true);
        } catch (err) {
            setMessages((m) => [
                ...m,
                {
                    role: "assistant",
                    content:
                        "Sorry — I couldn't reach the assistant just now. Please try again, or email us directly.",
                    escalate: true,
                    topic: "error",
                },
            ]);
            setEscalated(true);
        } finally {
            setBusy(false);
        }
    };

    const reset = () => {
        const fresh = newSessionId();
        localStorage.setItem(SESSION_KEY, fresh);
        localStorage.removeItem(MSGS_KEY);
        setMessages([]);
        setEscalated(false);
        window.location.reload();
    };

    const showWelcome = messages.length === 0;
    const supportEmail = settings?.contact_email || "care@hahnemann.com";
    const brand = settings?.brand_name || "Hahnemann";

    return (
        <>
            {/* Floating launcher button */}
            {!open && (
                <button
                    type="button"
                    onClick={() => setOpen(true)}
                    aria-label="Open customer support chat"
                    data-testid="support-chat-launcher"
                    className="fixed bottom-6 left-6 z-40 group flex items-center gap-2 bg-sky-700 hover:bg-sky-800 text-stone-50 px-5 py-3 shadow-xl rounded-full transition-all hover:scale-105 hover:shadow-2xl"
                >
                    <MessageCircle className="w-5 h-5" />
                    <span className="text-sm font-medium tracking-wide">
                        Need help?
                    </span>
                </button>
            )}

            {/* Chat panel */}
            {open && (
                <div
                    role="dialog"
                    aria-label="Customer support chat"
                    data-testid="support-chat-panel"
                    className="fixed z-50 right-4 left-4 bottom-4 sm:right-auto sm:left-6 sm:bottom-6 sm:w-[400px] max-h-[80vh] flex flex-col bg-stone-50 border border-stone-300 shadow-2xl rounded-none overflow-hidden"
                >
                    {/* Header */}
                    <div className="bg-sky-800 text-stone-50 px-4 py-3 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <LifeBuoy className="w-5 h-5 text-amber-300" />
                            <div>
                                <div className="font-serif text-base leading-none">
                                    {brand} Helper
                                </div>
                                <div className="text-[10px] uppercase tracking-widest text-sky-200 mt-1">
                                    Customer support · AI assisted
                                </div>
                            </div>
                        </div>
                        <button
                            onClick={() => setOpen(false)}
                            aria-label="Close chat"
                            data-testid="support-chat-close"
                            className="hover:bg-sky-700 p-1 transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Messages */}
                    <div
                        ref={scrollRef}
                        className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-stone-50"
                        data-testid="support-chat-messages"
                    >
                        {showWelcome && (
                            <div className="text-stone-700 text-sm space-y-3">
                                <p>
                                    Hi! I can help with orders, shipping,
                                    accounts, distributor enrollment, and how
                                    to use the site.
                                </p>
                                <p className="text-stone-500 text-xs">
                                    For symptoms or remedy questions, please
                                    use{" "}
                                    <Link
                                        to="/ai-doctor"
                                        className="underline text-sky-700 hover:text-sky-800"
                                        onClick={() => setOpen(false)}
                                    >
                                        Dr. Hahnemann AI
                                    </Link>
                                    .
                                </p>
                                <div className="grid grid-cols-1 gap-2 pt-2">
                                    {QUICK_PROMPTS.map((q) => (
                                        <button
                                            key={q}
                                            type="button"
                                            onClick={() => send(q)}
                                            className="text-left text-xs px-3 py-2 border border-stone-300 hover:border-sky-700 hover:text-sky-700 transition-colors"
                                            data-testid={`support-quick-${q
                                                .replace(/[^a-z]/gi, "-")
                                                .toLowerCase()}`}
                                        >
                                            {q}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {messages.map((m, i) => (
                            <div
                                key={i}
                                className={`flex ${
                                    m.role === "user"
                                        ? "justify-end"
                                        : "justify-start"
                                }`}
                            >
                                <div
                                    className={`${
                                        m.role === "user"
                                            ? "bg-sky-700 text-stone-50"
                                            : "bg-white text-stone-900 border border-stone-200"
                                    } px-3 py-2 max-w-[85%] text-sm whitespace-pre-wrap leading-relaxed`}
                                    data-testid={`support-msg-${m.role}`}
                                >
                                    {m.content}
                                </div>
                            </div>
                        ))}

                        {busy && (
                            <div className="flex justify-start">
                                <div className="bg-white border border-stone-200 px-3 py-2 text-stone-500 text-sm flex items-center gap-2">
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                    thinking…
                                </div>
                            </div>
                        )}

                        {escalated && (
                            <div
                                className="border-l-4 border-amber-500 bg-amber-50 px-3 py-2 text-xs text-stone-800"
                                data-testid="support-escalate-banner"
                            >
                                <div className="font-medium text-amber-900 mb-1">
                                    Talk to a human
                                </div>
                                <p>
                                    Email us at{" "}
                                    <a
                                        href={`mailto:${supportEmail}`}
                                        className="underline text-sky-700"
                                        data-testid="support-email-link"
                                    >
                                        {supportEmail}
                                    </a>{" "}
                                    and our team will get back within one
                                    business day.
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Composer */}
                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            send();
                        }}
                        className="border-t border-stone-200 bg-white px-3 py-3 flex items-end gap-2"
                    >
                        <textarea
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && !e.shiftKey) {
                                    e.preventDefault();
                                    send();
                                }
                            }}
                            placeholder="Type your question…"
                            rows={1}
                            className="flex-1 resize-none text-sm border border-stone-300 px-3 py-2 focus:outline-none focus:border-sky-700 max-h-32"
                            data-testid="support-chat-input"
                            disabled={busy}
                        />
                        <button
                            type="submit"
                            disabled={busy || !draft.trim()}
                            className="bg-sky-700 hover:bg-sky-800 disabled:bg-stone-300 disabled:cursor-not-allowed text-stone-50 px-3 py-2 transition-colors"
                            data-testid="support-chat-send"
                            aria-label="Send message"
                        >
                            {busy ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <Send className="w-4 h-4" />
                            )}
                        </button>
                    </form>

                    {messages.length > 0 && (
                        <button
                            type="button"
                            onClick={reset}
                            className="text-[11px] text-stone-500 hover:text-stone-800 py-2 border-t border-stone-100 bg-stone-50"
                            data-testid="support-chat-reset"
                        >
                            Start a new conversation
                        </button>
                    )}
                </div>
            )}
        </>
    );
}
